import { ArrowLeft, Mail, MessageCircle, Phone, ChevronDown } from 'lucide-react';
import { useState } from 'react';

interface HelpCenterScreenProps {
  onBack: () => void;
}

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[#E5E7EB] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F7F8FA] transition-colors"
      >
        <span className="font-medium text-[#111827] pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#6B7280] flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <p className="text-sm text-[#6B7280]">{answer}</p>
        </div>
      )}
    </div>
  );
};

export const HelpCenterScreen = ({ onBack }: HelpCenterScreenProps) => {
  const faqs = [
    {
      question: '¿Cómo puedo cancelar mi boleto?',
      answer: 'Puedes cancelar tu boleto desde "Mis boletos" hasta 2 horas antes de la salida. El reembolso depende de las políticas de cancelación del servicio.',
    },
    {
      question: '¿Puedo cambiar la fecha de mi viaje?',
      answer: 'Sí, puedes cambiar la fecha de tu viaje sin costo adicional hasta 24 horas antes de la salida, sujeto a disponibilidad.',
    },
    {
      question: '¿Qué pasa si pierdo mi boleto?',
      answer: 'Tu boleto electrónico está siempre disponible en la aplicación. También lo enviamos a tu correo electrónico como respaldo.',
    },
    {
      question: '¿Cómo funciona el proceso de abordaje?',
      answer: 'Presenta tu boleto electrónico (código QR) al conductor al momento de abordar. También puedes mostrarlo desde tu correo electrónico.',
    },
    {
      question: '¿Qué equipaje puedo llevar?',
      answer: 'Cada pasajero puede llevar una maleta grande en la bodega y una mochila o bolsa pequeña en cabina, sin costo adicional.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-[#111827]" />
          </button>
          <h1 className="text-lg font-bold text-[#111827]">Centro de ayuda</h1>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 pb-8">
        {/* Contact Cards */}
        <div className="space-y-3">
          <a
            href="mailto:soporte@mexmo.com"
            className="block bg-white rounded-[16px] border border-[#E5E7EB] p-4 hover:border-[#FF6A2A] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FFF4ED] rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#FF6A2A]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#111827]">Envíanos un correo</p>
                <p className="text-sm text-[#6B7280]">soporte@mexmo.com</p>
              </div>
            </div>
          </a>

          <a
            href="tel:+525512345678"
            className="block bg-white rounded-[16px] border border-[#E5E7EB] p-4 hover:border-[#FF6A2A] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FFF4ED] rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-[#FF6A2A]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#111827]">Llámanos</p>
                <p className="text-sm text-[#6B7280]">+52 55 1234 5678</p>
              </div>
            </div>
          </a>

          <button className="w-full bg-white rounded-[16px] border border-[#E5E7EB] p-4 hover:border-[#FF6A2A] transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FFF4ED] rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-[#FF6A2A]" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-[#111827]">Chat en vivo</p>
                <p className="text-sm text-[#6B7280]">Lun - Vie, 9:00 - 18:00</p>
              </div>
            </div>
          </button>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-[16px] border border-[#E5E7EB] overflow-hidden">
          <div className="p-4 border-b border-[#E5E7EB]">
            <h2 className="font-bold text-[#111827]">Preguntas frecuentes</h2>
          </div>
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </div>
  );
};
